import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { BehaviorSubject, Observable, catchError, forkJoin, of, take } from 'rxjs';

import { UserQuery } from '@common/store/user.store';
import { BroadcastLogRow, NotificationItem, SocketNotificationData, UserProfile } from '@common/types';

import { ApiService } from './api.service';
import { UserPreferencesService } from './user-preferences.service';

const BROADCAST_LOG_ENDPOINT = '/api/core/config/broadcast_log';

/** AK-715: the panel renders at most this many rows. Unread is counted over the full fetched set. */
export const NOTIFICATION_LIST_CAP = 50;

/**
 * AIWB-1257: a reload keeps local arrivals received at most this long before its fetch started. The backend
 * writes broadcast_log within milliseconds of the emit, so anything older is already in the fetch.
 */
export const NOTIFICATION_RELOAD_OVERLAP_MS = 5_000;

/**
 * AK-715 DEV FLAG — flip to true to drop the room filters and see every notification in the
 * entity, not just the ones addressed to this user. Never commit this as `true`: the endpoint
 * is not scoped server-side, so it returns other users' notifications.
 */
const DEV_SHOW_ALL_NOTIFICATIONS = false;

/**
 * AK-715: state for the notification centre.
 *
 * Two sources feed one list:
 *  - `load()` seeds history from the broadcast_log config table
 *  - `add()` receives live arrivals from the global socket handler in AppComponent
 *
 * Both dedupe on `broadcast_id`, because a notification can arrive over the socket while the
 * initial fetch is still in flight.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationCenterService {
  private readonly apiService = inject(ApiService);
  private readonly userQuery = inject(UserQuery);
  private readonly userPreferencesService = inject(UserPreferencesService);

  private readonly items = new BehaviorSubject<NotificationItem[]>([]);
  readonly items$: Observable<NotificationItem[]> = this.items.asObservable();

  /** Every fetched notification, uncapped — `items$` is the capped view for rendering. */
  private readonly allItems = new BehaviorSubject<NotificationItem[]>([]);

  private readonly loadFailed = new BehaviorSubject<boolean>(false);
  readonly loadFailed$: Observable<boolean> = this.loadFailed.asObservable();

  private readonly unreadCount = new BehaviorSubject<number>(0);
  readonly unreadCount$: Observable<number> = this.unreadCount.asObservable();

  private entityId = '';

  /** Seed the list from the broadcast log. Safe to call before the user is known — it no-ops. */
  load(): void {
    this.userQuery.user$.pipe(take(1)).subscribe((user: UserProfile) => {
      const userId = user?.user_id;
      if (!userId) {
        return;
      }

      this.entityId = user?.entity?.entity_id ?? '';

      const userRoom = DEV_SHOW_ALL_NOTIFICATIONS
        ? new HttpParams().set('message_type', 'notification')
        : new HttpParams().set('message_type', 'notification').set('room_type', 'user').set('room_id', userId);

      const entityRoom = new HttpParams().set('message_type', 'notification').set('room_type', 'entity');

      const fetchStartedAt = Date.now();

      forkJoin({
        user: this.fetch(userRoom),
        entity: DEV_SHOW_ALL_NOTIFICATIONS ? of([]) : this.fetch(entityRoom),
      }).subscribe(({ user: userRows, entity: entityRows }) => {
        if (userRows === null || entityRows === null) {
          this.loadFailed.next(true);
          return;
        }

        this.loadFailed.next(false);

        const fetched = [...userRows, ...entityRows].map((r) => this.toItem(r));
        // Socket arrivals that landed while this fetch was in flight are carried over: the
        // backend writes the broadcast log AFTER it sends the socket message, so an in-flight
        // fetch structurally cannot contain them and replacing the list outright would drop a
        // notification the user has already been shown as a toast. The envelope has no
        // broadcast_id, so the server row for a live notification never dedupes against its
        // `local-…` copy: carrying every local item would duplicate each one on every reload
        // (AIWB-1257 reloads on every reconnect). Only arrivals the in-flight fetch structurally
        // cannot contain are carried. Fetched rows come first so the server's copy wins the
        // broadcast_id dedupe once the backend sends real ids.
        const live = this.allItems
          .getValue()
          .filter(
            (item) => item.receivedLocally && (item.receivedAt ?? 0) >= fetchStartedAt - NOTIFICATION_RELOAD_OVERLAP_MS,
          );

        const merged = this.dedupeAndSort([...fetched, ...live]);
        this.allItems.next(merged);
        this.items.next(merged.slice(0, NOTIFICATION_LIST_CAP));
        this.recount();
      });
    });
  }

  /** null signals a failed request — distinct from an empty result. */
  private fetch(params: HttpParams): Observable<BroadcastLogRow[] | null> {
    return this.apiService
      .callApi<BroadcastLogRow[]>('get', BROADCAST_LOG_ENDPOINT, undefined, params)
      .pipe(catchError(() => of(null)));
  }

  private toItem(row: BroadcastLogRow): NotificationItem {
    return {
      broadcast_id: row.broadcast_id,
      created_ts: NotificationCenterService.normaliseTs(row.created_ts),
      data: (row.data ?? {}) as SocketNotificationData,
      source: row.source,
      priority: row.priority,
    };
  }

  /**
   * AIWB-1257: created_ts must be a string for the localeCompare sort and the last-seen watermark —
   * anything else becomes '' (sorts last).
   */
  private static normaliseTs(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  /** Dedupe by broadcast_id and sort newest first. Capping happens at the call site. */
  private dedupeAndSort(incoming: NotificationItem[]): NotificationItem[] {
    const byId = new Map<string, NotificationItem>();
    incoming.forEach((item) => {
      if (!byId.has(item.broadcast_id)) {
        byId.set(item.broadcast_id, item);
      }
    });

    return [...byId.values()].sort((a, b) => (b.created_ts || '').localeCompare(a.created_ts || ''));
  }

  /**
   * Stamp the watermark at the newest notification we hold, so everything currently
   * fetched reads as seen. No-op on an empty list — stamping "now" there would silently
   * suppress older notifications that arrive in a later fetch.
   */
  markAllSeen(): void {
    const newest = this.allItems.getValue()[0];
    if (!newest || !this.entityId) {
      return;
    }

    this.userPreferencesService.setNotificationsLastSeen(this.entityId, newest.created_ts);
    this.recount();
  }

  private recount(): void {
    const lastSeen = this.entityId ? this.userPreferencesService.getNotificationsLastSeen(this.entityId) : null;

    const unread = lastSeen
      ? this.allItems.getValue().filter((item) => (item.created_ts || '').localeCompare(lastSeen) > 0).length
      : this.allItems.getValue().length;

    this.unreadCount.next(unread);
  }

  /**
   * Record a notification that arrived over the socket. Deduped against the fetched history
   * because an arrival can race the initial load. The server envelope carries `timestamp` and never
   * broadcast_id / created_ts (AIWB-1257), so the id is synthesised and created_ts falls back to the
   * envelope timestamp, then to the receipt time.
   */
  add(
    data: SocketNotificationData,
    envelope?: { broadcast_id?: string; created_ts?: unknown; timestamp?: unknown },
  ): void {
    const item: NotificationItem = {
      broadcast_id: envelope?.broadcast_id || `local-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      created_ts:
        NotificationCenterService.normaliseTs(envelope?.created_ts) ||
        NotificationCenterService.normaliseTs(envelope?.timestamp) ||
        new Date().toISOString(),
      data: data ?? { message: '' },
      receivedLocally: true,
      receivedAt: Date.now(),
    };

    const existing = this.allItems.getValue();
    if (existing.some((i) => i.broadcast_id === item.broadcast_id)) {
      return;
    }

    const merged = this.dedupeAndSort([item, ...existing]);
    this.allItems.next(merged);
    this.items.next(merged.slice(0, NOTIFICATION_LIST_CAP));
    this.recount();
  }
}
